import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import { Users, ArrowUpRight } from "lucide-react";

export default function CreatorsDirectory() {
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/creators").then((res) => setCreators(res.data)).catch(() => setCreators([])).finally(() => setLoading(false));
  }, []);

  return (
    <div className="relative min-h-screen">
      <div className="glow-bg" />
      <Header />
      <main className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10 pt-32 pb-24">
        <div className="mb-14">
          <div className="flex items-center gap-2 mb-6">
            <span className="w-8 h-px bg-glow-accent" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-glow-accent">Community</span>
          </div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white">
            CREATOR <span className="text-glow-accent drop-shadow-[0_0_24px_rgba(250,255,0,0.4)]">VAULTS</span>
          </h1>
          <p className="mt-6 font-mono text-sm text-white/60 max-w-2xl leading-relaxed">
            Explore galleries curated by G.L.O.W creators. Each vault is a personal collection.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => <div key={i} className="h-40 bg-surface border border-white/10 animate-pulse" />)}
          </div>
        ) : creators.length === 0 ? (
          <div className="border border-dashed border-white/15 py-32 text-center" data-testid="creators-empty">
            <Users className="w-10 h-10 text-white/20 mx-auto mb-6" />
            <p className="font-heading font-500 text-xl text-white/70 uppercase tracking-widest">No Creators Yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="creators-directory">
            {creators.map((c, i) => (
              <motion.div key={c.user_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: (i % 6) * 0.05 }}>
                <Link
                  to={`/profile/${c.user_id}`}
                  className="group flex items-center gap-4 bg-surface border border-white/10 p-6 hover:border-glow-accent/50 hover:-translate-y-1 hover:shadow-[0_0_24px_rgba(250,255,0,0.12)] transition-[transform,border-color,box-shadow] duration-300"
                  data-testid={`creator-card-${c.user_id}`}
                >
                  {c.picture ? (
                    <img src={c.picture} alt={c.name} className="w-14 h-14 rounded-full border border-white/20 object-cover" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-glow-accent/20 flex items-center justify-center font-heading text-glow-accent text-xl">
                      {(c.name || "?").charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h3 className="font-heading font-700 text-lg text-white truncate">{c.name || "Creator"}</h3>
                    <p className="font-mono text-xs text-white/50 mt-1">
                      {c.item_count} {c.item_count === 1 ? "collectible" : "collectibles"}
                    </p>
                  </div>
                  <ArrowUpRight className="w-5 h-5 text-white/30 group-hover:text-glow-accent transition-colors" />
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
